import type { Labelled } from './common.js';

/**
 * حذف السجلات.
 *
 * الحذف في نظام أعمال ليس زرًّا: السجل الواحد مرتبط بغيره، وحذفه يجرّ معه
 * ما لم يقصده أحد. جدول العملاء وحده يتتالى إلى عشرة جداول، منها الفواتير
 * والتحصيلات — فزرُّ حذفٍ بلا حارس يمحو دفترًا محاسبيًا بنقرة.
 *
 * فالقاعدة هنا ثلاث درجات:
 *
 *   يُمنع    — للسجل أثرٌ مالي أو محاسبي. لا يُحذف بحال، ويُعرض البديل.
 *   يُحذّر   — يُحذف ومعه توابعه، وتُعرض التوابع بأسمائها وأعدادها أولًا.
 *   يُحذف    — لا شيء يتبعه.
 *
 * والبديل عن الحذف الممنوع هو التعطيل: السجل يخرج من القوائم والنماذج
 * ويبقى في الدفاتر، وهو ما يريده من طلب الحذف في أغلب الحالات.
 */

/* ==========================================================================
   ما يُحذف
   ========================================================================== */

export const DELETABLE_ENTITIES = [
  'lead',
  'account',
  'contact',
  'deal',
  'quotation',
  'contract',
] as const;
export type DeletableEntity = (typeof DELETABLE_ENTITIES)[number];

export interface DeletableDef extends Labelled<DeletableEntity> {
  /** اسم السجل الواحد، للرسائل. */
  singular: string;
  /** أدنى دور يحذف. */
  minRole: 'MANAGER' | 'ADMIN';
  /** البديل المعروض حين يُمنع الحذف. */
  alternative: string;
}

export const DELETABLE_DEFS: readonly DeletableDef[] = [
  {
    key: 'lead',
    label: 'العملاء المحتملون',
    singular: 'العميل المحتمل',
    kind: 'info',
    minRole: 'MANAGER',
    alternative: 'غيّر حالته إلى «مستبعد» ليخرج من قوائم المتابعة ويبقى في السجل.',
  },
  {
    key: 'account',
    label: 'العملاء',
    singular: 'العميل',
    kind: 'info',
    minRole: 'MANAGER',
    alternative: 'عطّله من ملفه ليخرج من القوائم والنماذج وتبقى فواتيره في الدفاتر.',
  },
  {
    key: 'contact',
    label: 'جهات الاتصال',
    singular: 'جهة الاتصال',
    kind: 'neutral',
    minRole: 'MANAGER',
    alternative: 'انقلها إلى عميل آخر إن كانت انتقلت، بدل حذف تاريخها.',
  },
  {
    key: 'deal',
    label: 'الصفقات',
    singular: 'الصفقة',
    kind: 'info',
    minRole: 'MANAGER',
    alternative: 'أغلقها بنتيجة «خسارة» مع ذكر السبب — فذلك ما يغذّي تحليل الخسائر.',
  },
  {
    key: 'quotation',
    label: 'عروض الأسعار',
    singular: 'عرض السعر',
    kind: 'info',
    minRole: 'MANAGER',
    alternative: 'غيّر حالته إلى «مرفوض» ليبقى في سجل ما عُرض على العميل.',
  },
  {
    key: 'contract',
    label: 'العقود',
    singular: 'العقد',
    kind: 'info',
    minRole: 'MANAGER',
    alternative: 'ألغِ العقد بدل حذفه، فيبقى أثره ويظهر أنه لاغٍ.',
  },
] as const;

export const DELETABLE_MAP = Object.fromEntries(
  DELETABLE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DeletableEntity, DeletableDef>>;

/* ==========================================================================
   نتيجة الفحص
   ========================================================================== */

export interface DeletionDependent {
  /** اسم النوع التابع بالعربية. */
  label: string;
  count: number;
}

export interface DeletionCheck {
  entity: DeletableEntity;
  /** اسم السجل نفسه، ليُعرض في التأكيد. */
  name: string;
  /**
   * ما يمنع الحذف.
   *
   * وجود واحد يكفي للمنع: لا تُوزن الآثار المالية بعضها ببعض.
   */
  blockers: DeletionDependent[];
  /** ما سيُحذف مع السجل. */
  cascades: DeletionDependent[];
  /**
   * ما يبقى ويفقد ارتباطه بالسجل وحده.
   *
   * الفصل عن `cascades` ليس تدقيقًا لغويًا. المخطط يفرّق بين `Cascade`
   * و`SetNull`، وكانت النافذة تعرض الاثنين تحت «ويُحذف معه»: فيُقال لمن
   * يحذف صفقةً إن عرضَي سعرٍ سيُحذفان معها، ثم يجدهما بعد الحذف قائمين بلا
   * صفقة. نافذة تأكيدٍ تصف ما لا يقع أسوأ من نافذة لا تصف شيئًا: الأولى
   * تُبنى عليها قرارات.
   */
  unlinks: DeletionDependent[];
  canDelete: boolean;
  /** البديل المقترح حين يُمنع. */
  alternative: string | null;
}

/**
 * صياغة سبب المنع.
 *
 * دالة خالصة تُستعمل في الخادم والواجهة معًا، فلا تفترق الرسالة بينهما.
 */
export function blockedReason(check: DeletionCheck): string | null {
  if (check.canDelete || !check.blockers.length) return null;

  const parts = check.blockers.map((b) => `${b.count} ${b.label}`).join('، و');
  const def = DELETABLE_MAP[check.entity];

  return `لا يُحذف ${def.singular} «${check.name}» لارتباطه بـ${parts}. ${def.alternative}`;
}

/** مجموع ما سيُحذف تبعًا. */
export function cascadeTotal(check: DeletionCheck): number {
  return check.cascades.reduce((sum, c) => sum + c.count, 0);
}
